import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  api,
  type Domain,
  type DnsStatus,
  type DnsChange,
  type Report,
  type Source,
  type PaginatedResponse,
} from '../lib/api';
import s from './DomainDetailPage.module.css';

/* -------------------------------------------------------------------------- */
/*  Types                                                                      */
/* -------------------------------------------------------------------------- */

type TabId = 'overview' | 'reports' | 'sources' | 'dns';

/* -------------------------------------------------------------------------- */
/*  Tab: Overview                                                              */
/* -------------------------------------------------------------------------- */

function OverviewTab({ domain, dns }: { domain: Domain; dns: DnsStatus | null }) {
  return (
    <>
      {/* Stats */}
      <div className={s.statsGrid}>
        <div className={s.statCard}>
          <div className={s.statLabel}>Messages</div>
          <div className={s.statValue}>&mdash;</div>
        </div>
        <div className={s.statCard}>
          <div className={s.statLabel}>Pass Rate</div>
          <div className={s.statValue}>&mdash;</div>
        </div>
        <div className={s.statCard}>
          <div className={s.statLabel}>Sources</div>
          <div className={s.statValue}>&mdash;</div>
        </div>
      </div>

      {/* DNS records */}
      <div className={s.dnsSection}>
        <div className={s.dnsLabel}>
          SPF Record
          {dns && (
            <span className={dns.spf.valid ? s.dnsValid : dns.spf.record ? s.dnsInvalid : s.dnsMissing}>
              {dns.spf.valid ? 'valid' : dns.spf.record ? 'invalid' : 'missing'}
            </span>
          )}
        </div>
        {dns?.spf.record ? (
          <div className={s.dnsRecord}>{dns.spf.record}</div>
        ) : (
          <div className={s.dnsEmpty}>No SPF record detected</div>
        )}
      </div>

      <div className={s.dnsSection}>
        <div className={s.dnsLabel}>
          DKIM
          {dns && (
            <span className={dns.dkim.valid ? s.dnsValid : s.dnsMissing}>
              {dns.dkim.valid ? 'valid' : 'not detected'}
            </span>
          )}
        </div>
        {dns?.dkim.selectors && dns.dkim.selectors.length > 0 ? (
          <div className={s.dnsRecord}>{dns.dkim.selectors.join(', ')}</div>
        ) : (
          <div className={s.dnsEmpty}>No DKIM selectors detected</div>
        )}
      </div>

      <div className={s.dnsSection}>
        <div className={s.dnsLabel}>
          DMARC Record
          {dns && (
            <span className={dns.dmarc.valid ? s.dnsValid : dns.dmarc.record ? s.dnsInvalid : s.dnsMissing}>
              {dns.dmarc.valid ? 'valid' : dns.dmarc.record ? 'invalid' : 'missing'}
            </span>
          )}
        </div>
        {dns?.dmarc.record ? (
          <div className={s.dnsRecord}>{dns.dmarc.record}</div>
        ) : (
          <div className={s.dnsEmpty}>No DMARC record detected</div>
        )}
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*  Tab: Reports                                                               */
/* -------------------------------------------------------------------------- */

function ReportsTab({ domainId }: { domainId: string }) {
  const [data, setData] = useState<PaginatedResponse<Report> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const limit = 20;

  const fetchReports = useCallback(() => {
    setLoading(true);
    api.reports
      .list({ domainId, page, limit })
      .then((res) => {
        setData(res);
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load reports');
        setLoading(false);
      });
  }, [domainId, page]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  if (loading) return <div className={s.emptyState}>Loading reports&hellip;</div>;
  if (error) return <div className={s.error}>{error}</div>;
  if (!data || data.data.length === 0) {
    return <div className={s.emptyState}>No reports yet</div>;
  }

  const totalPages = Math.ceil(data.total / limit);

  return (
    <>
      <div className={s.tableWrap}>
        <table className={s.table}>
          <thead>
            <tr>
              <th>Date Range</th>
              <th>Reporter</th>
              <th>Messages</th>
              <th>Pass</th>
              <th>Fail</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {data.data.map((r) => (
              <tr key={r.id}>
                <td style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                  {new Date(r.dateRange.begin).toLocaleDateString()} &ndash;{' '}
                  {new Date(r.dateRange.end).toLocaleDateString()}
                </td>
                <td>{r.orgName}</td>
                <td>{r.recordCount}</td>
                <td style={{ color: 'var(--success-text)' }}>{r.passCount}</td>
                <td style={{ color: r.failCount > 0 ? 'var(--danger-text)' : undefined }}>
                  {r.failCount}
                </td>
                <td style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                  {new Date(r.createdAt).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className={s.pagination}>
          <button
            type="button"
            className={s.pageBtn}
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </button>
          <span className={s.pageInfo}>
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            className={s.pageBtn}
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*  Tab: Sources                                                               */
/* -------------------------------------------------------------------------- */

function classificationBadge(source: Source): { label: string; cls: string } {
  if (source.identified && source.label === 'authorized') {
    return { label: 'authorized', cls: s.badgeSuccess };
  }
  if (source.identified && source.label === 'unauthorized') {
    return { label: 'unauthorized', cls: s.badgeDanger };
  }
  if (source.label === 'forwarding') {
    return { label: 'forwarding', cls: s.badgeInfo };
  }
  return { label: 'unknown', cls: s.badgeWarning };
}

function SourcesTab({ domainId }: { domainId: string }) {
  const [data, setData] = useState<PaginatedResponse<Source> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const limit = 20;

  const fetchSources = useCallback(() => {
    setLoading(true);
    api.sources
      .list({ domainId, page, limit })
      .then((res) => {
        setData(res);
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load sources');
        setLoading(false);
      });
  }, [domainId, page]);

  useEffect(() => {
    fetchSources();
  }, [fetchSources]);

  if (loading) return <div className={s.emptyState}>Loading sources&hellip;</div>;
  if (error) return <div className={s.error}>{error}</div>;
  if (!data || data.data.length === 0) {
    return <div className={s.emptyState}>No sources yet</div>;
  }

  const totalPages = Math.ceil(data.total / limit);

  return (
    <>
      <div className={s.tableWrap}>
        <table className={s.table}>
          <thead>
            <tr>
              <th>IP Address</th>
              <th>Org</th>
              <th>Classification</th>
              <th>Messages</th>
              <th>Pass Rate</th>
              <th>Disposition</th>
            </tr>
          </thead>
          <tbody>
            {data.data.map((src) => {
              const badge = classificationBadge(src);
              return (
                <tr key={src.id}>
                  <td className={s.mono}>{src.sourceIp}</td>
                  <td>{src.orgName ?? '—'}</td>
                  <td>
                    <span className={badge.cls}>{badge.label}</span>
                  </td>
                  <td>{src.count}</td>
                  <td>{src.passRate.toFixed(1)}%</td>
                  <td>{src.disposition}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className={s.pagination}>
          <button
            type="button"
            className={s.pageBtn}
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </button>
          <span className={s.pageInfo}>
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            className={s.pageBtn}
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*  Tab: DNS                                                                   */
/* -------------------------------------------------------------------------- */

function DnsTab({
  dns,
  domainId,
}: {
  dns: DnsStatus | null;
  domainId: string;
}) {
  const [changes, setChanges] = useState<PaginatedResponse<DnsChange> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.dns
      .changes({ domainId })
      .then((res) => {
        setChanges(res);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [domainId]);

  return (
    <>
      {/* Current records */}
      {dns && (
        <>
          <div className={s.dnsSection}>
            <div className={s.dnsLabel}>
              SPF
              <span className={dns.spf.valid ? s.dnsValid : dns.spf.record ? s.dnsInvalid : s.dnsMissing}>
                {dns.spf.valid ? 'valid' : dns.spf.record ? 'invalid' : 'missing'}
              </span>
            </div>
            {dns.spf.record ? (
              <div className={s.dnsRecord}>{dns.spf.record}</div>
            ) : (
              <div className={s.dnsEmpty}>Not detected</div>
            )}
          </div>

          <div className={s.dnsSection}>
            <div className={s.dnsLabel}>
              DMARC
              <span className={dns.dmarc.valid ? s.dnsValid : dns.dmarc.record ? s.dnsInvalid : s.dnsMissing}>
                {dns.dmarc.valid ? 'valid' : dns.dmarc.record ? 'invalid' : 'missing'}
              </span>
            </div>
            {dns.dmarc.record ? (
              <div className={s.dnsRecord}>{dns.dmarc.record}</div>
            ) : (
              <div className={s.dnsEmpty}>Not detected</div>
            )}
            {dns.dmarc.policy && (
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', marginTop: 'var(--space-1)' }}>
                Policy: {dns.dmarc.policy}
              </div>
            )}
          </div>

          <div className={s.dnsSection}>
            <div className={s.dnsLabel}>
              DKIM
              <span className={dns.dkim.valid ? s.dnsValid : s.dnsMissing}>
                {dns.dkim.valid ? 'valid' : 'not detected'}
              </span>
            </div>
            {dns.dkim.selectors.length > 0 ? (
              <div className={s.dnsRecord}>{dns.dkim.selectors.join(', ')}</div>
            ) : (
              <div className={s.dnsEmpty}>No selectors detected</div>
            )}
          </div>
        </>
      )}

      {/* Change history */}
      <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 600, color: 'var(--text-primary)', margin: 'var(--space-6) 0 var(--space-4)' }}>
        Change History
      </h3>

      {loading ? (
        <div className={s.emptyState}>Loading changes&hellip;</div>
      ) : !changes || changes.data.length === 0 ? (
        <div className={s.emptyState}>No DNS changes recorded</div>
      ) : (
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Record Type</th>
                <th>Field</th>
                <th>Old Value</th>
                <th>New Value</th>
              </tr>
            </thead>
            <tbody>
              {changes.data.map((c) => (
                <tr key={c.id}>
                  <td style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                    {new Date(c.detectedAt).toLocaleDateString()}
                  </td>
                  <td>{c.recordType}</td>
                  <td>{c.field}</td>
                  <td className={s.mono}>{c.oldValue || '—'}</td>
                  <td className={s.mono}>{c.newValue || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*  DomainDetailPage                                                           */
/* -------------------------------------------------------------------------- */

export default function DomainDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [domain, setDomain] = useState<Domain | null>(null);
  const [dns, setDns] = useState<DnsStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabId>('overview');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    Promise.all([
      api.domains.get(id),
      api.dns.status(id).catch(() => ({ statuses: [] })),
    ])
      .then(([domainRes, dnsRes]) => {
        if (cancelled) return;
        setDomain(domainRes.domain);
        setDns(dnsRes.statuses[0] ?? null);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load domain');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) return <div className={s.loading}>Loading&hellip;</div>;
  if (error) return <div className={s.error}>{error}</div>;
  if (!domain || !id) return <div className={s.error}>Domain not found</div>;

  const ruaAddress = `${domain.id}@reports.dmarcopolo.com`;

  function handleCopyRua() {
    navigator.clipboard.writeText(ruaAddress).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const dotClass =
    domain.status === 'active'
      ? s.dotActive
      : domain.status === 'pending'
        ? s.dotPending
        : s.dotError;

  const TABS: { id: TabId; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'reports', label: 'Reports' },
    { id: 'sources', label: 'Sources' },
    { id: 'dns', label: 'DNS' },
  ];

  return (
    <>
      {/* Back link */}
      <Link to="/app/domains" className={s.backLink}>
        &larr; Domains
      </Link>

      {/* Header */}
      <div className={s.header}>
        <div className={s.headerLeft}>
          <div className={s.domainRow}>
            <span className={s.domainName}>{domain.domain}</span>
            <span className={`${s.statusDot} ${dotClass}`} />
          </div>
          <div className={s.ruaRow}>
            <span className={s.ruaAddress}>{ruaAddress}</span>
            <button type="button" className={s.copyBtn} onClick={handleCopyRua}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className={s.tabs}>
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={tab === t.id ? s.tabActive : s.tab}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'overview' && <OverviewTab domain={domain} dns={dns} />}
      {tab === 'reports' && <ReportsTab domainId={id} />}
      {tab === 'sources' && <SourcesTab domainId={id} />}
      {tab === 'dns' && <DnsTab dns={dns} domainId={id} />}
    </>
  );
}
